/**
 * 路由级错误边界（包 Suspense + 懒加载页面）。
 *
 * 主要场景：发版后仍在旧页面的用户点击跳转时，懒加载 chunk 的 URL 已失效
 * （hash 变化 → 404 → dynamic import reject）。若无路由级边界，错误会一路
 * 冒泡到根边界，整站降级为全屏错误页；此处局部兜底，提供「重试」与
 * 「重新加载」——chunk 失效类错误必须整页刷新才能拉到新 manifest，
 * 仅重试（重挂子树）无法恢复。
 */

import type { ReactNode } from "react";
import ErrorBoundary from "./ErrorBoundary";
import i18n from "../i18n";

/** 动态 import 失败的常见错误特征（Chrome/Firefox/Safari 文案各不同） */
function isChunkLoadError(error: Error): boolean {
  const msg = `${error.name} ${error.message}`.toLowerCase();
  return (
    msg.includes("failed to fetch dynamically imported module") ||
    msg.includes("importing a module script failed") ||
    msg.includes("loading chunk") ||
    msg.includes("loading css chunk")
  );
}

export default function RouteErrorBoundary({ children }: { children: ReactNode }) {
  return (
    <ErrorBoundary
      fallback={(error, reset) => (
        <div className="error-boundary-fallback" role="alert">
          <p>{i18n.t("errors.boundaryDefault")}</p>
          <p>
            {!isChunkLoadError(error) && (
              <button type="button" onClick={reset}>
                {i18n.t("common.retry")}
              </button>
            )}{" "}
            <button type="button" onClick={() => window.location.reload()}>
              {i18n.t("common.reload")}
            </button>
          </p>
        </div>
      )}
    >
      {children}
    </ErrorBoundary>
  );
}
